import React from "react";
import { StyleSheet, View, Platform, type StyleProp, type ViewStyle } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { ThemedText } from "../../ThemedText";
import { ThemedButton } from "../../ThemedButton";
import { ThemedToolTip } from "../../ThemedToolTip";
import { useThemeColor } from "@/hooks/useThemeColor";

export type CaptureMode = "keyboard" | "audio";

export interface ICaptureModeToolbarProps {
  visible?: boolean;
  activeMode?: CaptureMode;
  isRecording?: boolean;
  onSelectMode?: (mode: CaptureMode) => void;
  onToggleRecording?: () => void;
  isStandalonePreview?: boolean;
  style?: StyleProp<ViewStyle>;
}

/**
 * CaptureModeToolbar
 *
 * Dedicated toolbar switching between standard keyboard typing and speech-to-text
 * voice transcription audio capture.
 */
export const CaptureModeToolbar: React.FC<ICaptureModeToolbarProps> = ({
  visible = true,
  activeMode = "keyboard",
  isRecording = false,
  onSelectMode,
  onToggleRecording,
  isStandalonePreview = false,
  style,
}) => {
  const cardBg = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const tintColor = useThemeColor({}, "tint");
  const isDark = cardBg === "#131520";

  if (!visible) return null;

  const handleMouseDown = (e: any) => {
    if (Platform.OS === "web") {
      e.preventDefault();
    }
  };

  return (
    <View
      style={[
        styles.container,
        {
          backgroundColor: cardBg,
          borderColor,
          boxShadow: isDark
            ? "0 4px 16px rgba(0,0,0,0.5)"
            : "0 4px 16px rgba(0,0,0,0.1)",
        } as any,
        isStandalonePreview && styles.standaloneContainer,
        style,
      ]}
      onMouseDown={handleMouseDown}
    >
      <ThemedText style={styles.sectionLabel}>Input Mode:</ThemedText>

      {/* Keyboard Input */}
      <ThemedToolTip tooltip="Standard Keyboard & Mouse Input">
        <ThemedButton
          title="Keyboard"
          variant={activeMode === "keyboard" ? "primary" : "ghost"}
          icon={<Ionicons name="keypad-outline" size={15} color={activeMode === "keyboard" ? "#fff" : tintColor} />}
          onPress={() => onSelectMode && onSelectMode("keyboard")}
          style={styles.actionBtn}
        />
      </ThemedToolTip>

      {/* Audio Input / Speech to Text */}
      <ThemedToolTip tooltip="Voice Input: Record & Transcribe Audio">
        <ThemedButton
          title={isRecording ? "Listening..." : "Audio Input"}
          variant={activeMode === "audio" ? "primary" : "ghost"}
          icon={
            <Ionicons
              name={isRecording ? "radio-outline" : "mic-outline"}
              size={15}
              color={activeMode === "audio" ? "#fff" : isRecording ? "#DC2626" : tintColor}
            />
          }
          onPress={() => {
            if (onSelectMode) onSelectMode("audio");
            if (onToggleRecording) onToggleRecording();
          }}
          style={[styles.actionBtn, isRecording && styles.recordingBtn]}
        />
      </ThemedToolTip>

      {isRecording && (
        <View style={styles.recordingPulse}>
          <View style={styles.recordingDot} />
          <ThemedText style={styles.recordingText}>Transcribing Live</ThemedText>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingVertical: 4,
    minHeight: 38,
    gap: 6,
  },
  standaloneContainer: {
    position: "relative",
    top: 0,
    left: 0,
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: "700",
    opacity: 0.6,
    marginRight: 4,
  },
  actionBtn: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    minHeight: 28,
  },
  recordingBtn: {
    backgroundColor: "rgba(220, 38, 38, 0.15)",
  },
  recordingPulse: {
    flexDirection: "row",
    alignItems: "center",
    marginLeft: 6,
    gap: 4,
  },
  recordingDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: "#DC2626",
  },
  recordingText: {
    fontSize: 11,
    fontWeight: "700",
    color: "#DC2626",
  },
});
